/**
 * 검수용 PDF 미리보기 패널 (M7-C2).
 *
 * 책임:
 *   - 페이지 네비게이션 (1-based, clamp to [1, page_count])
 *   - 줌 컨트롤 (fit-to-width 토글 + discrete 7단계: 50/75/100/125/150/175/200)
 *   - 원본 PDF 다운로드 (Authorization 필요 → blob fetch → <a download>)
 *   - ImagePagePreview 를 감싸서 overflow-auto 스크롤 영역 제공
 *
 * 책임 외:
 *   - bbox overlay (M11)
 *   - resize handle (M7-C3 의 ResizableSplit 책임)
 *   - 모바일 stack 레이아웃 (M7-C4 의 EditRecordModal 책임)
 */

import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Maximize2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { downloadPdfBlob } from "@/lib/api/file_images";
import type { FieldBbox } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";

import BboxOverlay from "./BboxOverlay";
import type { EditableFieldKey } from "./fieldLabels";
import ImagePagePreview from "./ImagePagePreview";
import { usePagesMeta } from "./usePagesMeta";
import { usePrefetchPageImage } from "./usePageImage";

interface Props {
  jobId: string;
  fileId: string;
  /** 다운로드 파일명 (원본 PDF 이름 유지 — 비ASCII 도 그대로). */
  originalName: string;
  /**
   * 필드별 bbox (M11). ``undefined``/``null`` 이면 워커가 매칭을 아직 수행하지
   * 않은 레코드 — overlay 미표시.
   *
   * key 가 dict 에 존재하면서 값이 ``null`` 이면 "매칭 시도했으나 실패" 의미
   * (UI 상 회색 점 배지로 노출 — EditRecordModal 책임).
   */
  bboxes?: Record<string, FieldBbox | null> | null;
  /** focus 된 필드 (강한 강조). */
  activeFieldKey?: EditableFieldKey | null;
  /** hover 된 필드 (임시 강조). */
  hoveredFieldKey?: EditableFieldKey | null;
  /** bbox 클릭 → 폼 필드 focus 로 점프 (F6). */
  onBboxClick?: (key: EditableFieldKey) => void;
  /** bbox hover → 폼 필드 강조 (F4 양방향). */
  onBboxHover?: (key: EditableFieldKey | null) => void;
}

const ZOOM_LEVELS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0] as const;
type ZoomLevel = (typeof ZOOM_LEVELS)[number];
type Zoom = "fit" | ZoomLevel;

const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.indexOf(1.0);

/** ZOOM_LEVELS 인덱스 접근의 단일 안전 진입점.
 *  delta 만큼 step 이동하고 [0, LEN-1] 로 clamp. */
function stepZoom(prev: Zoom, delta: -1 | 1): ZoomLevel {
  const cur = typeof prev === "number" ? ZOOM_LEVELS.indexOf(prev) : DEFAULT_ZOOM_INDEX;
  const nextIdx = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, cur + delta));
  // 위 clamp 로 항상 유효 인덱스 보장 → noUncheckedIndexedAccess 단언.
  return ZOOM_LEVELS[nextIdx] as ZoomLevel;
}

export default function PdfPreviewPanel({
  jobId,
  fileId,
  originalName,
  bboxes,
  activeFieldKey,
  hoveredFieldKey,
  onBboxClick,
  onBboxHover,
}: Props) {
  const { t } = useTranslation();
  const meta = usePagesMeta(jobId, fileId);
  const pageCount = meta.data?.page_count ?? 1;

  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [naturalWidth, setNaturalWidth] = useState<number | null>(null);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // 매칭 결과 (백엔드 csv_bbox) — prop 그대로. null = OCR 미수행 → overlay 미표시.
  const effectiveBboxes = bboxes ?? null;

  // ── 드래그 팬 (Acrobat Hand Tool 패턴) ────────────────────────────────
  // 휠/트랙패드 스크롤은 native 로 동작하고, 마우스 좌클릭 드래그로 보조 이동을 제공.
  // fit 모드에서는 스크롤이 불가능하므로 비활성.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
    pointerId: number;
  } | null>(null);

  const isPannable = zoom !== "fit";

  const safePage = Math.max(1, Math.min(page, pageCount));

  // ── 인접 페이지 prefetch (멀티페이지 PDF 페이지 넘김 즉시성) ──────
  const prefetchPage = usePrefetchPageImage();
  useEffect(() => {
    if (safePage > 1) prefetchPage(jobId, fileId, safePage - 1);
    if (safePage < pageCount) prefetchPage(jobId, fileId, safePage + 1);
  }, [jobId, fileId, safePage, pageCount, prefetchPage]);

  // ── M11 양방향 자동 스크롤 (form input → PDF bbox) ─────────────────
  // activeFieldKey 변경 시 (폼 input focus 또는 bbox 클릭) 해당 bbox 를 viewport
  // 안으로 가져옴. 정책:
  //   1) 매칭 실패 (`null`) 인 필드는 스크롤 안 함 (회색 점 배지가 시각 신호).
  //   2) 다른 페이지에 있으면 page 자동 전환 → 이미지 로드 (naturalSize 갱신) 후
  //      재실행 시 같은 페이지 분기로 진입.
  //   3) 이미 viewport 안이면 스크롤 안 함 (사용자가 직접 클릭한 bbox 가 가운데로
  //      재정렬되는 어색함 방지). 줌인 상태에서 화면 밖이면 smooth 스크롤.
  useEffect(() => {
    if (!activeFieldKey || !effectiveBboxes) return;
    const b = effectiveBboxes[activeFieldKey];
    if (!b) return;
    if (b.page !== safePage) {
      setPage(b.page);
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>(`[data-field="${activeFieldKey}"]`);
    if (!el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const inView =
      eRect.top >= cRect.top &&
      eRect.bottom <= cRect.bottom &&
      eRect.left >= cRect.left &&
      eRect.right <= cRect.right;
    if (inView) return;
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  }, [activeFieldKey, effectiveBboxes, safePage, naturalWidth, naturalHeight]);

  // ── 키보드 단축키 ─────────────────────────────────────────────────
  // ←/→  : 페이지 이동
  // +/=  : 줌인        -/_ : 줌아웃        0 : fit 토글
  // (input/textarea/select/contentEditable 에 포커스가 있으면 무시 — 폼 편집 우선)
  useEffect(() => {
    const isEditableTarget = (t: EventTarget | null): boolean => {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (t.isContentEditable) return true;
      return false;
    };

    const bumpZoom = (delta: -1 | 1) => {
      setZoom((prev) => stepZoom(prev, delta));
    };

    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return; // 다른 단축키와 충돌 방지

      switch (e.key) {
        // 페이지 넘김은 PageUp/PageDown 으로 처리한다.
        // (←/→ 는 EditRecordModal 의 '다음/이전 안건' 이동 전용 — 영수증 페이지
        //  수와 무관하게 안건 이동이 되도록 방향키 충돌을 제거)
        case "PageUp":
          setPage((p) => Math.max(1, p - 1));
          e.preventDefault();
          break;
        case "PageDown":
          setPage((p) => Math.min(pageCount, p + 1));
          e.preventDefault();
          break;
        case "+":
        case "=":
          bumpZoom(1);
          e.preventDefault();
          break;
        case "-":
        case "_":
          bumpZoom(-1);
          e.preventDefault();
          break;
        case "0":
          setZoom("fit");
          e.preventDefault();
          break;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageCount]);

  const goPrev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goNext = useCallback(() => setPage((p) => Math.min(pageCount, p + 1)), [pageCount]);

  const zoomOut = () => setZoom((prev) => stepZoom(prev, -1));
  const zoomIn = () => setZoom((prev) => stepZoom(prev, 1));
  const setFit = () => setZoom("fit");

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadPdfBlob(jobId, fileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalName || "invoice.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // revoke 는 anchor click 후 충분히 지연 (브라우저 다운로드 시작 보장)
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : (err as Error).message;
      setDownloadError(msg);
    } finally {
      setDownloading(false);
    }
  };

  // 주의: Tailwind preflight 가 모든 <img> 에 max-width: 100% 를 강제하므로
  // 줌이 부모 폭을 넘어가야 하는 경우에는 maxWidth:"none" 으로 명시적 해제 필요.
  const imgStyle: CSSProperties =
    zoom === "fit"
      ? { width: "100%", height: "auto", maxWidth: "100%" }
      : naturalWidth
        ? {
            width: `${Math.round(naturalWidth * zoom)}px`,
            height: "auto",
            maxWidth: "none",
          }
        : { width: "auto", height: "auto", maxWidth: "none" };

  const zoomLabel = zoom === "fit" ? "fit" : `${Math.round(zoom * 100)}%`;

  // ── Pointer 드래그 핸들러 ──
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isPannable) return;
    // 마우스 좌클릭만. 터치/펜은 native 스크롤(pinch-zoom 등) 우선.
    if (e.pointerType !== "mouse" || e.button !== 0) return;

    const container = containerRef.current;
    if (!container) return;

    // 입력 가능 자식 (현재는 없지만 향후 overlay form 등 대비)
    const target = e.target as HTMLElement;
    if (target.closest("button, input, textarea, select, a")) return;

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startScrollLeft: container.scrollLeft,
      startScrollTop: container.scrollTop,
      pointerId: e.pointerId,
    };
    container.setPointerCapture(e.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const container = containerRef.current;
    if (!container) return;
    container.scrollLeft = drag.startScrollLeft - (e.clientX - drag.startX);
    container.scrollTop = drag.startScrollTop - (e.clientY - drag.startY);
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    containerRef.current?.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  };

  // 더블클릭 = fit ↔ 100% 토글 (toolbar 버튼은 위에 있어 여기 안 옴)
  const handleDoubleClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("button, input, textarea, select, a")) return;
    setZoom((prev) => (prev === "fit" ? 1.0 : "fit"));
  };

  const viewportStyle: CSSProperties = {
    cursor: !isPannable ? "default" : isDragging ? "grabbing" : "grab",
    userSelect: isDragging ? "none" : "auto",
  };

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-1.5 text-xs">
        {/* 페이지 네비 */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goPrev}
            disabled={safePage <= 1}
            className="rounded p-1 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30"
            aria-label={t("preview.toolbar.prevPage")}
            title={t("preview.toolbar.prevPageTitle")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[40px] text-center tabular-nums">
            {safePage} / {pageCount}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={safePage >= pageCount}
            className="rounded p-1 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30"
            aria-label={t("preview.toolbar.nextPage")}
            title={t("preview.toolbar.nextPageTitle")}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <span className="text-slate-300">|</span>

        {/* 줌 */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={zoomOut}
            className="rounded p-1 text-slate-500 transition hover:bg-slate-100"
            aria-label={t("preview.toolbar.zoomOut")}
            title={t("preview.toolbar.zoomOutTitle")}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="min-w-[44px] text-center tabular-nums">{zoomLabel}</span>
          <button
            type="button"
            onClick={zoomIn}
            className="rounded p-1 text-slate-500 transition hover:bg-slate-100"
            aria-label={t("preview.toolbar.zoomIn")}
            title={t("preview.toolbar.zoomInTitle")}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={setFit}
            className={cn(
              "rounded p-1 transition hover:bg-slate-100",
              zoom === "fit" ? "text-blue-600" : "text-slate-500",
            )}
            aria-label={t("preview.toolbar.fitWidth")}
            title={t("preview.toolbar.fitWidthTitle")}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        {/* 다운로드 */}
        <div className="ml-auto flex items-center gap-2">
          {downloadError && (
            <span className="text-[11px] text-red-600" title={downloadError}>
              {t("preview.toolbar.downloadError")}
            </span>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            aria-label={t("preview.toolbar.downloadPdf")}
          >
            {downloading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            PDF
          </button>
        </div>
      </div>

      {/* 본문 — overflow-auto 스크롤 영역 + 드래그 팬 */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-slate-100 p-3"
        style={viewportStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        // 드래그 중 native image-drag 가 cancel 되도록 한 번 더 막음
        onDragStart={(e) => e.preventDefault()}
      >
        <ImagePagePreview
          jobId={jobId}
          fileId={fileId}
          page={safePage}
          imgStyle={imgStyle}
          onImageLoad={(w, h) => {
            setNaturalWidth(w);
            setNaturalHeight(h);
          }}
        >
          {effectiveBboxes && naturalWidth && naturalHeight && (
            <BboxOverlay
              bboxes={effectiveBboxes}
              pageW={naturalWidth}
              pageH={naturalHeight}
              currentPage={safePage}
              activeFieldKey={activeFieldKey}
              hoveredFieldKey={hoveredFieldKey}
              onClick={onBboxClick}
              onHover={onBboxHover}
            />
          )}
        </ImagePagePreview>
      </div>
    </div>
  );
}
